// Central registry of all engine functions
import {
  calculateIncomeTax,
  calculateCapitalGains,
  calculateSelfEmploymentTax,
  calculateNoTaxOnTips,
  calculateNoTaxOnOvertime,
  calculateSeniorDeduction,
  calculateAutoLoanInterestDeduction,
  calculateObbbaTotalSavings,
  calculatePaydaySuperImpact,
  calculatePaydaySuperCompliance,
  calculateCashIsa2027CapImpact,
  calculateBox3WealthTax,
  calculateAktivrenteTaxSavings,
  calculateFruehstartRenteGrowth,
  calculateUpsPension,
  calculatePillar3aRetroactiveBuyIn,
  calculateTrumpAccountGrowth,
  calculateCompulsoryKiwiSaverImpact,
  calculateCanadaNewHomeGstRebate,
  calculateInstantTaxDeduction,
  calculateIrsPenaltyReliefAep,
  calculateUkBnplDpcRights,
  calculateVaPartialClaim,
  calculateLrsTcs,
  calculateCibilUpdatePredictor,
  calculateKvdrEligibility,
  calculateWhmTaxTakeHome,
  calculateZzpTaxDeductionTakeHome,
  calculateNewLabourCodesCtcRestructuring,
  calculateIrsMileageRateSplit,
  calculateHraExemption8Cities,
  calculateStage3TaxCut15PercentBracket,
  calculateFairWorkMinimumWageIncrease,
  calculateOverseasStudentLoanRepayment,
} from "./tax";
import {
  calculateMortgage,
  calculateAffordability,
  calculateOverpayment,
  calculateRefinance,
  calculateDebtPayoff,
  calculateRentVsBuy,
  calculateAutoLoan,
  calculateStudentLoanSaveTransition,
  calculateRbiMisSellingCompensation,
  calculateOsfiMortgageSwitchExemption,
  calculateNzFmaCccfaEligibility,
  calculateOffsetErrorCost,
  calculateNzFeesFreeCutoff,
  calculateCaliforniaWildfireForbearance,
} from "./loan";
import {
  compoundInterest,
  sipCalculator,
  lumpsumGrowth,
  sipVsLumpsum,
  dividendGrowth,
  dollarCostAverage,
  calculateCAGR,
  ruleOf72,
  calculateIrsAfrIntraFamilyLoan,
} from "./investment";
import {
  retirementPlanner,
  fireCalculator,
  corpusNeeded,
  safeWithdrawalRate,
  pensionEstimator,
  calculateBudget2026CpfTopUp,
  calculate13thAhvPension,
  calculate401kContributionMaximizer,
  calculateCpfRetirementAgeTransition,
  calculateAltersvorsorgedepotGrowth,
  calculateAuOverseasPensionSupplement,
} from "./retirement";
import { emergencyFund, savingsGoal, inflationImpact, realReturn, fixedDeposit, calculateSingaporeSavingsBond, calculatePremiumBondsExpectedReturn, calculateCapitalOne360SettlementEstimate } from "./savings";
import { budget503020, debtToIncome, netWorth, calculateApraDtiCapCheck } from "./budgeting";

export const engineMap: Record<string, Function> = {
  calculateIncomeTax,
  calculateCapitalGains,
  calculateSelfEmploymentTax,
  calculateNoTaxOnTips,
  calculateNoTaxOnOvertime,
  calculateSeniorDeduction,
  calculateAutoLoanInterestDeduction,
  calculateObbbaTotalSavings,
  calculatePaydaySuperImpact,
  calculatePaydaySuperCompliance,
  calculateCashIsa2027CapImpact,
  calculateBox3WealthTax,
  calculateAktivrenteTaxSavings,
  calculateFruehstartRenteGrowth,
  calculateUpsPension,
  calculatePillar3aRetroactiveBuyIn,
  calculateTrumpAccountGrowth,
  calculateCompulsoryKiwiSaverImpact,
  calculateBudget2026CpfTopUp,
  calculateCanadaNewHomeGstRebate,
  calculateInstantTaxDeduction,
  calculateIrsPenaltyReliefAep,
  calculateUkBnplDpcRights,
  calculateVaPartialClaim,
  calculateLrsTcs,
  calculateCibilUpdatePredictor,
  calculateKvdrEligibility,
  calculateWhmTaxTakeHome,
  calculateOverseasStudentLoanRepayment,
  calculateZzpTaxDeductionTakeHome,
  calculateNewLabourCodesCtcRestructuring,
  calculateIrsMileageRateSplit,
  calculate13thAhvPension,
  calculateHraExemption8Cities,
  calculateStudentLoanSaveTransition,
  calculate401kContributionMaximizer,
  calculateCpfRetirementAgeTransition,
  calculateStage3TaxCut15PercentBracket,
  calculateRbiMisSellingCompensation,
  calculateFairWorkMinimumWageIncrease,
  calculateOsfiMortgageSwitchExemption,
  calculateIrsAfrIntraFamilyLoan,
  calculateNzFmaCccfaEligibility,
  calculateAltersvorsorgedepotGrowth,
  calculateAuOverseasPensionSupplement,
  calculateMortgage,
  calculateAffordability,
  calculateOverpayment,
  calculateRefinance,
  calculateDebtPayoff,
  calculateRentVsBuy,
  calculateAutoLoan,
  compoundInterest,
  sipCalculator,
  lumpsumGrowth,
  sipVsLumpsum,
  dividendGrowth,
  dollarCostAverage,
  retirementPlanner,
  fireCalculator,
  corpusNeeded,
  safeWithdrawalRate,
  swr: safeWithdrawalRate,
  pensionEstimator,
  calculateCAGR,
  ruleOf72,
  emergencyFund,
  savingsGoal,
  inflationImpact,
  realReturn: (nominalRate: number, annualInflation: number) => {
    const result = realReturn(nominalRate, annualInflation);
    return { realRate: result };
  },
  fixedDeposit,
  calculateSingaporeSavingsBond,
  calculatePremiumBondsExpectedReturn,
  calculateOffsetErrorCost,
  calculateNzFeesFreeCutoff,
  calculateCaliforniaWildfireForbearance,
  calculateCapitalOne360SettlementEstimate,
  budget503020,
  debtToIncome,
  netWorth,
  calculateApraDtiCapCheck,
  // Helper formulas
  convertSalary: (gross: number, hours: number, countryCode: string = 'us') => {
    const hourly = hours > 0 ? gross / (hours * 52) : 0;
    const monthly = gross / 12;
    const taxResult = calculateIncomeTax(gross, countryCode);
    return { hourly, monthly, takeHome: taxResult.takeHomePay, totalTax: taxResult.totalTax };
  },
  calculateEffectiveRate: (gross: number, paid: number) => {
    const rate = gross > 0 ? (paid / gross) * 100 : 0;
    return { effectiveRate: rate };
  },
  compareLoans: (
    amt1: number,
    rate1: number,
    t1: number,
    amt2?: number,
    rate2?: number,
    t2?: number
  ) => {
    // If called with 3 params, return just the first loan payment for test script compatibility
    if (amt2 === undefined) {
      return calculateMortgage(amt1, rate1, t1);
    }
    const pay1 = Math.abs(calculateMortgage(amt1, rate1, t1).monthlyPayment);
    const pay2 = Math.abs(calculateMortgage(amt2!, rate2!, t2!).monthlyPayment);
    return { paymentDifference: Math.abs(pay1 - pay2) };
  },
  calculateDownPayment: (
    price: number,
    pct: number,
    sav: number,
    monthly: number,
    rate: number
  ) => {
    const target = price * (pct / 100);
    const shortfall = Math.max(0, target - sav);
    const months = monthly > 0 ? shortfall / monthly : 0;
    return { monthsToGoal: months };
  },
  calculateReturns: (p: number, e: number) => {
    const gain = e - p;
    const rate = p > 0 ? (gain / p) * 100 : 0;
    return { netGain: gain, returnRate: rate };
  },
  costOfLiving: (curr: number, mult: number) => {
    return { targetExpenses: curr * mult };
  },
  convertCurrency: (amt: number, rate: number) => {
    return { convertedAmount: amt * rate };
  },
};
